import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import authApiService from "../../services/authServices";
import Modal from "../../utils/modalUtils";
import { jwtDecode } from "jwt-decode";
import { ThreeDots } from "react-loader-spinner";
import StripePaymentSection from "./StripePaymentSection";
import { formatSeasonDisplayName } from "../../utils/seasonName";

const Payment = () => {
  const [city, setCity] = useState(null);
  const [seasonData, setSeasonData] = useState([]);
  const [selectedValue, setSelectedValue] = useState("");
  const [userid, setUserId] = useState(null);
  const [cityId, setCityId] = useState(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [stripeBusy, setStripeBusy] = useState(false);
  const [step, setStep] = useState(1); // 1 = select season, 2 = pay

  const navigate = useNavigate();

  const getAuthUserId = () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return null;
      return jwtDecode(token)?.userId || null;
    } catch {
      return null;
    }
  };

  const getSelectedCityFromStorage = () => {
    try {
      const stored = localStorage.getItem("selectedCity");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const syncCityFromStorage = () => {
    const sc = getSelectedCityFromStorage();
    if (sc?.id) {
      setCityId(sc.id);
      setCity(sc.name);
    }
  };

  useEffect(() => {
    setUserId(getAuthUserId());
    syncCityFromStorage();
  }, []);

  useEffect(() => {
    const handleCityChange = () => syncCityFromStorage();
    window.addEventListener("cityChange", handleCityChange);
    return () => window.removeEventListener("cityChange", handleCityChange);
  }, []);

  useEffect(() => {
    const fetchSeasons = async () => {
      const currentUserId = userid || getAuthUserId();
      if (!currentUserId) {
        setIsDataLoading(false);
        return;
      }
      const sc = getSelectedCityFromStorage();
      const effectiveCityId = cityId || sc?.id;

      try {
        setIsDataLoading(true);
        const payload = { userId: String(currentUserId) };
        if (effectiveCityId) payload.cityId = String(effectiveCityId);
        const response = await authApiService.post("paymentCheck", payload);

        if (response?.code === 200 && response?.data) {
          const data = response.data;
          const seasons = Array.isArray(data) ? data : data?.seasons || [];
          setSeasonData(seasons.filter((s) => s.paymentDone === false));
          if (data?.cityName) setCity(data.cityName);
        } else {
          setSeasonData([]);
        }
      } catch (error) {
        console.error("Error fetching seasons:", error);
        setSeasonData([]);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchSeasons();
  }, [userid, cityId]);

  const selectedSeason = seasonData.find(
    (season) =>
      String(season.registration_id || season.id) === String(selectedValue)
  );

  const fee = selectedSeason?.regular_registration_fee;

  const handlePaymentSuccess = () => {
    Modal.fire({
      icon: "success",
      title: "Payment Successful",
      text: "You have been registered successfully!",
      confirmButtonText: "Go to Player Profile",
      confirmButtonColor: "#addd1e",
    }).then(() => {
      const currentUserId = userid || getAuthUserId();
      navigate(
        currentUserId
          ? `/carter/profile?userId=${currentUserId}`
          : "/carter/profile"
      );
    });
  };

  const goToProfile = () => {
    const currentUserId = userid || getAuthUserId();
    navigate(
      currentUserId
        ? `/carter/profile?userId=${currentUserId}`
        : "/carter/profile"
    );
  };

  const handleContinueToPay = () => {
    if (!selectedValue) {
      Modal.fire({
        icon: "warning",
        title: "Select a season",
        text: "Please choose a season before continuing to payment.",
        confirmButtonText: "OK",
        confirmButtonColor: "#addd1e",
      });
      return;
    }
    setStep(2);
  };

  return (
    <div className="page-container col-sm-9 checkout-page" style={{ position: "relative" }}>
      <style>{`
        .checkout-page.page-container {
          width: 100%;
          max-width: 100%;
        }
        .checkout-page .checkout-heading.heading-golfer {
          width: 100%;
          max-width: 100%;
         margin-top: -9px;
          box-sizing: border-box;
          padding: 17.5px 20px;
        }
        .checkout-page .checkout-heading.heading-golfer h2 {
          color: #fff;
          margin: 0;
          font-size: 25px;
          padding: 0;
          font-weight: 700;
        }
        .checkout-page .checkout-shell {
          width: 75%;
          max-width: 100%;
          margin: 0 0 40px;
          padding: 0;
        }
        .checkout-page .checkout-steps {
          display: flex;
          gap: 12px;
          margin: 30px 0;
          width: 100%;
        }
        .checkout-page .checkout-step-pill {
          flex: 1;
          text-align: center;
          padding: 12px 10px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          background: #e8e8e8;
          color: #666;
        }
        .checkout-page .checkout-step-pill.active {
          background: #7a9234;
          color: #fff;
        }
        .checkout-page .checkout-step-pill.done {
          background: #c5d69a;
          color: #333;
        }
        .checkout-page .checkout-panel {
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 10px;
          padding: 28px 32px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          width: 100%;
        }
        .checkout-page .checkout-panel h3 {
          margin: 0 0 6px;
          font-size: 20px;
          color: #333;
          font-weight: 700;
        }
        .checkout-page .checkout-hint {
          margin: 0 0 20px;
          color: #666;
          font-size: 14px;
          line-height: 1.45;
        }
        .checkout-page .checkout-label {
          display: block;
          font-weight: 600;
          font-size: 13px;
          color: #444;
          margin-bottom: 6px;
        }
        .checkout-page .checkout-select {
          width: 100%;
          max-width: 100%;
          padding: 12px 14px;
          border: 1px solid #ccc;
          border-radius: 8px;
          font-size: 15px;
          background: #fff;
        }
        .checkout-page .checkout-summary {
          margin-top: 20px;
          background: #f7f8f5;
          border-radius: 8px;
          padding: 18px 20px;
          width: 100%;
        }
        .checkout-page .checkout-summary-row {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 8px 0;
          font-size: 15px;
          color: #444;
        }
        .checkout-page .checkout-summary-row.total {
          border-top: 1px solid #ddd;
          margin-top: 8px;
          padding-top: 14px;
          font-size: 18px;
          font-weight: 700;
          color: #222;
        }
        .checkout-page .checkout-grid {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
          gap: 28px;
          align-items: start;
          width: 100%;
        }
        @media (max-width: 900px) {
          .checkout-page .checkout-grid {
            grid-template-columns: 1fr;
          }
        }
        .checkout-page .checkout-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 24px;
          align-items: center;
        }
        .checkout-page .checkout-primary {
          background: #7a9234;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 14px 32px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          min-width: 200px;
        }
        .checkout-page .checkout-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .checkout-page .checkout-primary:hover:not(:disabled) {
          background: #6a8130;
        }
        .checkout-page .checkout-secondary {
          background: #eee;
          color: #333;
          border: 1px solid #ccc;
          border-radius: 8px;
          padding: 14px 24px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }
        .checkout-page .checkout-secondary:hover {
          background: #e2e2e2;
        }
        .checkout-page .checkout-back {
          background: transparent;
          border: none;
          color: #7a9234;
          font-weight: 600;
          cursor: pointer;
          padding: 8px 0;
          margin-bottom: 12px;
          font-size: 14px;
        }
        .checkout-card-panel {
          margin-top: 4px;
          width: 100%;
        }
        .checkout-card-title {
          margin: 0 0 14px !important;
          font-size: 16px !important;
        }
        .checkout-pay-btn {
          width: 100%;
          margin-top: 20px;
          background: #7a9234;
          color: #fff;
          border: none;
          border-radius: 8px;
          padding: 16px 20px;
          font-size: 17px;
          font-weight: 700;
          cursor: pointer;
        }
        .checkout-pay-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .checkout-pay-btn:hover:not(:disabled) {
          background: #6a8130;
        }
        .checkout-secure-note {
          margin: 12px 0 0;
          font-size: 12px;
          color: #888;
          text-align: center;
        }
        .checkout-error {
          color: #c0392b;
          padding: 8px 0;
        }
        .checkout-loading {
          color: #666;
          padding: 8px 0;
        }
        .checkout-login-warn {
          color: #c0392b;
          padding: 0 4px 12px;
        }
      `}</style>

      {stripeBusy && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <ThreeDots height="80" width="80" radius="9" color="#4fa94d" visible />
            <p style={{ marginTop: 20, color: "#4fa94d", fontSize: 18, fontWeight: 600 }}>
              Processing payment…
            </p>
          </div>
        </div>
      )}

      <div className="heading-golfer checkout-heading">
        <h2>Season Registration</h2>
      </div>

      <div className="section checkout-shell">
        {!getAuthUserId() && (
          <p className="checkout-login-warn">
            Please log in to pay and register for a season.
          </p>
        )}

        <div className="checkout-steps">
          <div className={`checkout-step-pill ${step === 1 ? "active" : "done"}`}>
            1. Select season
          </div>
          <div className={`checkout-step-pill ${step === 2 ? "active" : ""}`}>
            2. Pay
          </div>
        </div>

        {step === 1 && (
          <div className="checkout-panel">
            <h3>Choose your season</h3>
            <p className="checkout-hint">
              Pick the division you want to play in. You can play at your level
              or one level up — not down.
            </p>

            <label className="checkout-label" htmlFor="registration">
              City: {city || "—"}
            </label>
            <select
              id="registration"
              className="checkout-select"
              value={selectedValue}
              onChange={(e) => {
                const id = e.target.value ? parseInt(e.target.value, 10) : "";
                setSelectedValue(id);
              }}
              disabled={stripeBusy || isDataLoading}
            >
              <option value="" disabled>
                Please select a season
              </option>
              {isDataLoading ? (
                <option value="">Loading seasons…</option>
              ) : seasonData.length > 0 ? (
                seasonData.map((season) => (
                  <option
                    key={season.registration_id || season.id}
                    value={season.registration_id || season.id}
                  >
                    {formatSeasonDisplayName(season.name)} — ${season.regular_registration_fee}
                  </option>
                ))
              ) : (
                <option value="">No seasons available</option>
              )}
            </select>

            {selectedSeason && (
              <div className="checkout-summary">
                <div className="checkout-summary-row">
                  <span>Season</span>
                  <span>{selectedSeason.name}</span>
                </div>
                <div className="checkout-summary-row">
                  <span>City</span>
                  <span>{city || "—"}</span>
                </div>
                <div className="checkout-summary-row total">
                  <span>Total due</span>
                  <span>${Number(fee || 0).toFixed(2)}</span>
                </div>
              </div>
            )}

            <div className="checkout-actions">
              <button
                type="button"
                className="checkout-primary"
                onClick={handleContinueToPay}
                disabled={!selectedValue || isDataLoading}
              >
                Continue to payment
              </button>
              <button
                type="button"
                className="checkout-secondary"
                onClick={goToProfile}
                disabled={stripeBusy}
              >
                Register later
              </button>
            </div>
          </div>
        )}

        {step === 2 && selectedSeason && (
          <div className="checkout-panel">
            <button
              type="button"
              className="checkout-back"
              onClick={() => setStep(1)}
              disabled={stripeBusy}
            >
              ← Change season
            </button>

           

            <div className="checkout-grid">
              <div>
              <h3>Checkout</h3>
            <p className="checkout-hint">
              Enter your card details to complete registration.
            </p>
                <div className="checkout-summary" style={{ marginTop: 0 }}>
                  <div className="checkout-summary-row">
                    <span>Season</span>
                    <span>{selectedSeason.name}</span>
                  </div>
                  <div className="checkout-summary-row">
                    <span>City</span>
                    <span>{city || "—"}</span>
                  </div>
                  <div className="checkout-summary-row total">
                    <span>Total due</span>
                    <span>${Number(fee || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="checkout-actions">
                  <button
                    type="button"
                    className="checkout-secondary"
                    onClick={goToProfile}
                    disabled={stripeBusy}
                  >
                    Register later
                  </button>
                </div>
              </div>

              <div>
                <StripePaymentSection
                  registrationId={selectedValue}
                  onSuccess={handlePaymentSuccess}
                  isProcessing={stripeBusy}
                  setIsProcessing={setStripeBusy}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment;
